import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  FiActivity,
  FiBarChart2,
  FiEye,
  FiFileText,
  FiLayers,
  FiUsers,
} from 'react-icons/fi';
import './home.page.css';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { usePermissions } from '../../../helpers/acl/permissions';
import { useCurrentFacility } from '../../../helpers/acl/facility';
import {
  recordToChartData,
  summaryCards,
  useHomeKpis,
} from '../../../helpers/api/analytics/home-kpis';
import { usePlatformMode } from '../../../hooks/usePlatformMode';
import { Card } from '../../ComponnentsUI/common/Card';
import {
  ColumnDef,
  DynamicTableCard,
} from '../../ComponnentsUI/common/DynamicTableCard';
import { ErrorDisplay } from '../../ComponnentsUI/common/ErrorDisplay';
import { LoadingSpinner } from '../../ComponnentsUI/common/LoadingSpinner';
import { StatCard } from '../../ComponnentsUI/common/StatCard';
import { GenericPieChart } from '../../ComponnentsUI/charts/GenericPieChart';
import Actions from '../../Sections/Actions/actions.section';
import {
  CompactDistributionBars,
  DailyCountChart,
  FacilityVolumeChart,
  FacilityViewsChart,
  HabitsStackedChart,
  HourDayHeatmap,
  MonthlyActivityChart,
  SimpleBarChart,
} from './components/HomeKpiCharts';
import {
  BiometryAverages,
  FacilityActivityTable,
  FacilityComparisonTable,
  GrowthSummary,
  GrowthWindowKey,
  HomeKpiTab,
  HomeKpiTabs,
  ScopePill,
} from './components/HomeKpiWidgets';
import { MoroccoMapPanel } from './components/MoroccoMapPanel';
import {
  AmnioticFluidCard,
  FetalHeartRateCard,
  PreEclampsiaScreeningCard,
} from './components/ClinicalStaticCards';
import { TrainingQualityTab } from './components/TrainingQualityTab';
import { AiCoverageTab } from './components/AiCoverageTab';
import { ClinicalDataToggleRow } from './components/ClinicalDataToggleRow';
import { isMoroccoMapEnabled } from '../../../helpers/runtimeConfig';
import ApiAsService from '../ApiAsService/apiasservice.page';
import {
  HomeKpisRecentPatient,
  HomeKpisRecentStudy,
} from '../../interfaces/homeKpis';

const PIE_COLORS = [
  '#3B82F6',
  '#8B5CF6',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#06B6D4',
  '#EC4899',
  '#84CC16',
  '#14B8A6',
  '#F97316',
  '#6366F1',
  '#A855F7',
  '#22D3EE',
  '#65A30D',
  '#EA580C',
  '#DB2777',
];

const formatDate = (dateString: string | null) => {
  if (!dateString) {
    return '-';
  }
  return new Date(dateString).toLocaleDateString();
};

const formatDateTime = (dateString: string | null) => {
  if (!dateString) {
    return '-';
  }
  return new Date(dateString).toLocaleString([], {
    dateStyle: 'short',
    timeStyle: 'short',
  });
};

const recentPatientsColumns = (
  navigate: (path: string) => void,
  t: (key: string) => string,
  allowView: boolean
): ColumnDef<HomeKpisRecentPatient>[] => [
  {
    header: t('tables.firstName'),
    colSpan: 3,
    cell: (patient) => (
      <div className="font-medium text-gray-800 dark:text-gray-200 truncate">
        {patient.first_name || '-'}
      </div>
    ),
  },
  {
    header: t('tables.lastName'),
    colSpan: 3,
    cell: (patient) => (
      <div className="text-gray-600 dark:text-gray-400 truncate">
        {patient.last_name || '-'}
      </div>
    ),
  },
  {
    header: t('tables.dateOfBirth'),
    colSpan: 3,
    cell: (patient) => (
      <div className="text-gray-600 dark:text-gray-400">
        {formatDate(patient.date_of_birth)}
      </div>
    ),
  },
  {
    header: t('tables.actions'),
    colSpan: 3,
    cell: (patient) => (
      <button
        onClick={() => allowView && navigate(`/patients/${patient.id}`)}
        className={`flex items-center gap-1.5 text-xs font-semibold ${
          allowView
            ? 'text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300'
            : 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
        }`}
        disabled={!allowView}
      >
        <FiEye size={14} />
        <span>{t('tables.view')}</span>
      </button>
    ),
  },
];

const recentStudiesColumns = (
  navigate: (path: string) => void,
  t: (key: string) => string,
  allowView: boolean
): ColumnDef<HomeKpisRecentStudy>[] => [
  {
    header: t('tables.studyId'),
    colSpan: 5,
    cell: (study) => (
      <div className="font-medium text-blue-600 dark:text-blue-400 truncate">
        {study.study_instance_uid}
      </div>
    ),
  },
  {
    header: t('tables.patientId'),
    colSpan: 3,
    cell: (study) => (
      <div className="text-gray-600 dark:text-gray-400 truncate">
        {study.patient_id}
      </div>
    ),
  },
  {
    header: t('tables.studyDate'),
    colSpan: 2,
    cell: (study) => (
      <div className="text-gray-600 dark:text-gray-400">
        {formatDateTime(study.study_date)}
      </div>
    ),
  },
  {
    header: t('tables.views'),
    colSpan: 2,
    cell: (study) => (
      <button
        className={`flex items-center gap-1.5 text-xs font-bold ${
          allowView
            ? 'text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400'
            : 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
        }`}
        onClick={() =>
          allowView &&
          navigate('/exam/viewer?StudyInstanceUIDs=' + study.study_instance_uid)
        }
        disabled={!allowView}
      >
        <FiEye size={14} className="text-gray-400 dark:text-gray-500" />
        <span>{study.views_detected}</span>
      </button>
    ),
  },
];

const AnalyticsDashboard: React.FC = () => {
  const { t } = useTranslation('Dashboard');
  useDocumentTitle(t('documentTitle'));
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { facilities } = useCurrentFacility();
  const canViewStudies = can('study.read');
  const canViewPatients = can('patient.read');
  const showFacilitiesRecent = facilities.length > 1 || can('audit_log.read');
  const showMap = isMoroccoMapEnabled();
  const [activeTab, setActiveTab] = useState<HomeKpiTab>(
    showMap ? 'map' : 'activity'
  );
  const [growthWindow, setGrowthWindow] = useState<GrowthWindowKey>('growthOverBaseline');
  const { data, isLoading, isFetching, error } = useHomeKpis();

  useEffect(() => {
    if (!showFacilitiesRecent && activeTab === 'facilities-recent') {
      setActiveTab('activity');
    }
    if (!showMap && activeTab === 'map') {
      setActiveTab('activity');
    }
    if (!showMap && activeTab === 'training-quality') {
      setActiveTab('activity');
    }
  }, [activeTab, showFacilitiesRecent, showMap]);

  const presentationData = useMemo(
    () => recordToChartData(data?.clinical.fetal_presentation_distribution),
    [data]
  );
  const placentaData = useMemo(
    () => recordToChartData(data?.clinical.placenta_localization_distribution),
    [data]
  );
  const pregnancyTypeData = useMemo(
    () => recordToChartData(data?.clinical.pregnancy_type),
    [data]
  );
  const trimesterData = useMemo(
    () => recordToChartData(data?.clinical.trimester_distribution),
    [data]
  );
  const ageGroupData = useMemo(
    () => recordToChartData(data?.demographics.age_group),
    [data]
  );
  const originData = useMemo(
    () => recordToChartData(data?.demographics.woman_origin),
    [data]
  );
  const maritalData = useMemo(
    () => recordToChartData(data?.demographics.marital_status),
    [data]
  );
  const educationData = useMemo(
    () => recordToChartData(data?.demographics.education_level),
    [data]
  );
  const consanguinityData = useMemo(
    () => recordToChartData(data?.demographics.consanguinity),
    [data]
  );
  const insuranceData = useMemo(
    () => recordToChartData(data?.demographics.insurance_coverage),
    [data]
  );
  const familyHistoryData = useMemo(
    () => recordToChartData(data?.demographics.family_history),
    [data]
  );
  const facilityShareData = useMemo(
    () =>
      data?.facilities.comparison.map((facility) => ({
        name: facility.facility_name,
        value: facility.total_studies,
      })) || [],
    [data]
  );
  if (isLoading) {
    return <LoadingSpinner />;
  }
  if (!data) {
    return (
      <ErrorDisplay message={error?.message || t('kpis.errors.fetchFailed')} />
    );
  }

  const stats = summaryCards(data.summary);
  const tabLabels: Record<HomeKpiTab, string> = {
    map: t('kpis.tabs.map'),
    activity: t('kpis.tabs.activity'),
    clinical: t('kpis.tabs.clinical'),
    'training-quality': t('kpis.tabs.trainingQuality'),
    'ai-views': t('kpis.tabs.aiCoverage'),
    population: t('kpis.tabs.population'),
    'facilities-recent': t('kpis.tabs.facilitiesRecent'),
  };

  return (
    <div className={`bg-card-color-200 dark:bg-gray-900 p-3 sm:p-4 lg:p-6 space-y-4 sm:space-y-6 transition-opacity ${isFetching ? 'opacity-60' : ''}`}>
      {isFetching && (
        <div className="fixed top-3 right-3 z-50 flex items-center gap-2 rounded-full bg-white/95 dark:bg-gray-800 px-3 py-1 text-xs text-gray-600 dark:text-gray-200 shadow-md ring-1 ring-black/5">
          <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-blue-500" />
          Updating...
        </div>
      )}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
        <div className="min-w-0 flex-1">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-title-page-color-100 dark:text-white">
              {t('kpis.title')}
            </h1>
            <ScopePill data={data} />
          </div>
          <p className="max-w-3xl text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            {t('kpis.subtitle')}
          </p>
        </div>
        <div className="flex shrink-0 items-center justify-end">
          <Actions />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6 sm:gap-4 lg:gap-5">
        <StatCard
          title={t('stats.totalPatients')}
          value={stats.patients.toLocaleString()}
          icon={<FiUsers />}
          color="#3B82F6"
        />
        <StatCard
          title={t('stats.totalStudies')}
          value={stats.exams.toLocaleString()}
          icon={<FiFileText />}
          color="#10B981"
        />
        <StatCard
          title={t('stats.reportsGenerated')}
          value={stats.reports.toLocaleString()}
          icon={<FiBarChart2 />}
          color="#F59E0B"
        />
        <StatCard
          title={t('kpis.stats.sweeps')}
          value={stats.sweeps.toLocaleString()}
          icon={<FiLayers />}
          color="#8B5CF6"
        />
        <StatCard
          title={t('stats.totalFrameViews')}
          value={stats.views.toLocaleString()}
          icon={<FiEye />}
          color="#06B6D4"
        />
        <StatCard
          title={t('kpis.stats.examsPerPatient')}
          value={stats.examsPerPatient.toLocaleString()}
          icon={<FiActivity />}
          color="#EF4444"
        />
      </div>

      <HomeKpiTabs
        active={activeTab}
        onChange={setActiveTab}
        labels={tabLabels}
        showFacilitiesRecent={showFacilitiesRecent}
        showMap={showMap}
      />

      {showMap && activeTab === 'map' && (
        <MoroccoMapPanel facilities={data.facilities.comparison} />
      )}

      {activeTab === 'activity' && (
        <div className="space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 sm:gap-6">
            <div className="lg:col-span-2">
              <Card title={t('kpis.charts.monthlyGrowth')}>
                <MonthlyActivityChart
                  data={data.activity.monthly_counts}
                  patientsLabel={t('analytics.patients')}
                  examsLabel={t('analytics.exams')}
                />
              </Card>
            </div>
            <Card title={t('kpis.cards.activitySnapshot')}>
              <div className="space-y-4">
                <div className="rounded-xl bg-card-color-200 dark:bg-gray-900/40 p-4">
                  <div className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                    {t('kpis.stats.latestActivity')}
                  </div>
                  <div className="mt-1 text-2xl font-bold text-title-page-color-100 dark:text-white">
                    {stats.latestActivityDate || '—'}
                  </div>
                </div>
                <div className="rounded-xl bg-card-color-200 dark:bg-gray-900/40 p-4">
                  <div className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                    {t('kpis.stats.viewsPerExam')}
                  </div>
                  <div className="mt-1 text-2xl font-bold text-title-page-color-100 dark:text-white">
                    {stats.viewsPerExam.toLocaleString()}
                  </div>
                </div>
              </div>
            </Card>
          </div>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 sm:gap-6">
            <Card title={t('kpis.charts.dailyExams')}>
              <DailyCountChart
                data={data.activity.daily_exam_counts}
                label={t('analytics.exams')}
                color="#10B981"
              />
            </Card>
            <Card title={t('kpis.charts.registrations')}>
              <DailyCountChart
                data={data.activity.daily_registrations}
                label={t('kpis.charts.newPatients')}
                color="#3B82F6"
              />
            </Card>
          </div>
          <Card title={t('kpis.charts.hourDayHeatmap')}>
            <HourDayHeatmap
              data={data.activity.hour_day_heatmap}
              examsLabel={t('analytics.exams')}
            />
          </Card>
          <Card title={t('kpis.cards.growthSummary')}>
            <GrowthSummary
              growth={data.activity.growth}
              windowKey={growthWindow}
              onWindowChange={setGrowthWindow}
            />
          </Card>
        </div>
      )}

      {activeTab === 'clinical' && (
        <div className="space-y-4 sm:space-y-6">
          {showMap && (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-3 sm:gap-6">
              <AmnioticFluidCard />
              <FetalHeartRateCard />
              <PreEclampsiaScreeningCard />
            </div>
          )}
          {showMap ? (
            <ClinicalDataToggleRow />
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-4 sm:gap-6">
              <Card title={t('charts.topFetalPresentations')}>
                <CompactDistributionBars
                  data={presentationData}
                  countLabel={t('kpis.charts.count')}
                />
              </Card>
              <Card title={t('kpis.charts.placenta')}>
                <GenericPieChart data={placentaData} colors={PIE_COLORS} />
              </Card>
              <Card title={t('charts.pregnancyType')}>
                <GenericPieChart data={pregnancyTypeData} colors={PIE_COLORS} />
              </Card>
              <Card title={t('kpis.charts.trimester')}>
                <GenericPieChart data={trimesterData} colors={PIE_COLORS} />
              </Card>
            </div>
          )}
          <Card title={t('kpis.cards.biometryAverages')}>
            <BiometryAverages values={data.clinical.biometry_averages_cm} />
          </Card>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-4 sm:gap-6">
            <Card title={t('kpis.charts.bpdDistribution')}>
              <SimpleBarChart
                data={data.clinical.biometry_distributions.bpd}
                label="BPD"
                color="#8B5CF6"
              />
            </Card>
            <Card title={t('kpis.charts.hcDistribution')}>
              <SimpleBarChart
                data={data.clinical.biometry_distributions.hc}
                label="HC"
                color="#3B82F6"
              />
            </Card>
            <Card title={t('kpis.charts.acDistribution')}>
              <SimpleBarChart
                data={data.clinical.biometry_distributions.ac}
                label="AC"
                color="#10B981"
              />
            </Card>
            <Card title={t('kpis.charts.flDistribution')}>
              <SimpleBarChart
                data={data.clinical.biometry_distributions.fl}
                label="FL"
                color="#F59E0B"
              />
            </Card>
          </div>
          <Card title={t('kpis.charts.efwDistribution')}>
            <SimpleBarChart
              data={data.clinical.biometry_distributions.efw}
              label="EFW"
              color="#EF4444"
              heightClassName="h-72"
            />
          </Card>
        </div>
      )}

      {showMap && activeTab === 'training-quality' && (
        <TrainingQualityTab training={data.training} />
      )}

      {activeTab === 'ai-views' && (
        <AiCoverageTab data={data} enableEnhanced={showMap} />
      )}

      {activeTab === 'population' && (
        <div className="space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3 sm:gap-6">
            <Card title={t('kpis.population.ageDistribution')}>
              <SimpleBarChart
                data={data.demographics.age_distribution}
                label={t('analytics.patients')}
                color="#8B5CF6"
              />
            </Card>
            <Card title={t('kpis.population.ageGroup')}>
              <GenericPieChart data={ageGroupData} colors={PIE_COLORS} />
            </Card>
            <Card title={t('kpis.population.origin')}>
              <GenericPieChart data={originData} colors={PIE_COLORS} />
            </Card>
          </div>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3 sm:gap-6">
            <Card title={t('kpis.population.maritalStatus')}>
              <GenericPieChart data={maritalData} colors={PIE_COLORS} />
            </Card>
            <Card title={t('kpis.population.educationLevel')}>
              <GenericPieChart data={educationData} colors={PIE_COLORS} />
            </Card>
            <Card title={t('kpis.population.consanguinity')}>
              <GenericPieChart data={consanguinityData} colors={PIE_COLORS} />
            </Card>
          </div>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2 sm:gap-6">
            <Card title={t('kpis.population.occupation')}>
              <SimpleBarChart
                data={data.demographics.occupation_top}
                label={t('analytics.patients')}
                color="#F59E0B"
                horizontal
                heightClassName="h-80"
              />
            </Card>
            <Card title={t('kpis.population.insurance')}>
              <GenericPieChart data={insuranceData} colors={PIE_COLORS} />
            </Card>
          </div>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2 sm:gap-6">
            <Card title={t('kpis.population.weightDistribution')}>
              <SimpleBarChart
                data={data.demographics.weight_distribution}
                label={t('analytics.patients')}
                color="#10B981"
              />
            </Card>
            <Card title={t('kpis.population.habits')}>
              <HabitsStackedChart
                data={data.demographics.habits_prevalence}
                labels={{
                  yes: t('kpis.population.yes'),
                  no: t('kpis.population.no'),
                  unknown: t('kpis.population.unknown'),
                }}
              />
            </Card>
          </div>
          <Card title={t('kpis.population.familyHistory')}>
            <SimpleBarChart
              data={familyHistoryData}
              label={t('analytics.patients')}
              color="#EF4444"
              horizontal
              heightClassName="h-80"
            />
          </Card>
        </div>
      )}

      {showFacilitiesRecent && activeTab === 'facilities-recent' && (
        <div className="space-y-4 sm:space-y-6">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2 sm:gap-6">
            <Card title={t('kpis.charts.facilityComparison')}>
              <FacilityVolumeChart
                data={data.facilities.comparison}
                patientsLabel={t('analytics.patients')}
                examsLabel={t('analytics.exams')}
                reportsLabel={t('analytics.reports')}
              />
            </Card>
            <Card title={t('kpis.tables.facilitySummary')}>
              <FacilityComparisonTable rows={data.facilities.comparison} />
            </Card>
          </div>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2 sm:gap-6">
            <Card title={t('kpis.charts.shareOfExams')}>
              <GenericPieChart data={facilityShareData} colors={PIE_COLORS} />
            </Card>
            <Card title={t('kpis.charts.aiViewsByFacility')}>
              <FacilityViewsChart
                data={data.facilities.comparison}
                label={t('stats.totalFrameViews')}
              />
            </Card>
          </div>
          <Card title={t('kpis.tables.perCentreActivity')}>
            <FacilityActivityTable rows={data.facilities.comparison} />
          </Card>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2 sm:gap-6">
            {canViewPatients && (
              <DynamicTableCard
                title={t('tables.recentPatients')}
                data={data.recent.patients}
                columns={recentPatientsColumns(navigate, t, canViewPatients)}
                viewMoreLink="/patients"
                getUniqueKey={(patient) => patient.id}
              />
            )}
            {canViewStudies && (
              <DynamicTableCard
                title={t('tables.recentStudies')}
                data={data.recent.studies}
                columns={recentStudiesColumns(navigate, t, canViewStudies)}
                viewMoreLink="/exams"
                getUniqueKey={(study) => study.study_instance_uid}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Home: React.FC = () => {
  const { mode: platformMode, loading: platformLoading } = usePlatformMode();

  if (platformLoading) {
    return <LoadingSpinner />;
  }

  if (platformMode === 'api_as_service') {
    return <ApiAsService />;
  }

  return <AnalyticsDashboard />;
};

export default Home;
